import React, { useEffect, useState, useCallback } from "react";
import {
    Typography,
    Paper,
    Box,
    Skeleton,
} from "@mui/material";
import { LineChart } from '@mui/x-charts/LineChart';
import DashboardEnroll from "../../component/Dashboard/Enroll";
import ProgramsList from "../../component/Dashboard/ProgramList";
import Registration from "../../component/Dashboard/Registration";
import ModuleHeading from "../../component/ModuleHeading";
import SupportDashboard from "../../component/Dashboard/SupportDashboard";
import { get } from "../../services/api-services";
import { formatDatecase } from "../../modules/ConvertDate";
import Datepicker from "../../component/DatePicker";

const dashboardCache = {
    data: null,
    filterdata: null
};

const Dashboard = () => {
    // Dashboard States
    const [totalUsers, setTotalUsers] = useState(dashboardCache.data?.totalUsers || 0);
    const [totalEnrollments, setTotalEnrollments] = useState(dashboardCache.data?.totalEnrollments || 0);
    const [totalPrograms, setTotalPrograms] = useState(dashboardCache.data?.totalPrograms || 0);
    const [paymentXLabel, setpaymentXLabel] = useState(dashboardCache.data?.paymentXLabel || []);
    const [paymentYLabel, setpaymentYLabel] = useState(dashboardCache.data?.paymentYLabel || []);
    const [enrollmentXLabel, setEnrollmentXLabel] = useState(dashboardCache.data?.enrollmentXLabel || []);
    const [userYLabel, setUserYLabel] = useState(dashboardCache.data?.userYLabel || []);
    const [invoiceYLabel, setInvoiceYLabel] = useState(dashboardCache.data?.invoiceYLabel || []);

    const [filterdata, setFilterdata] = useState(() => {
        if (dashboardCache.filterdata) return dashboardCache.filterdata;
        const now = new Date();
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return { from: lastMonth.toISOString(), to: now.toISOString() };
    });

    const [chartLoading, setChartLoading] = useState(!dashboardCache.data);

    const onGetDashboard = useCallback(async () => {
        if (!dashboardCache.data) setChartLoading(true);
        try {
            // 1. Fetch Payments (Income)
            const res = await get(`/Payments?filter={"where": {"paidOn" : { "between" : ["${filterdata?.from}", "${filterdata?.to}"] }},"order":"paidOn ASC"}`);
            let xLabels = [];
            let yLabels = [];

            if (res?.statusCode === 200) {
                const paymentData = res?.data || [];
                const aggregatedData = paymentData.reduce((acc, payment) => {
                    const dateKey = formatDatecase(payment.paidOn);
                    if (!acc[dateKey]) {
                        acc[dateKey] = 0;
                    }
                    acc[dateKey] += payment.amount;
                    return acc;
                }, {});
                xLabels = Object.keys(aggregatedData);
                yLabels = Object.values(aggregatedData);
            }
            setpaymentXLabel(xLabels);
            setpaymentYLabel(yLabels);

            // 2. Enrollment vs Users (Independent from Payments)
            const resUser = await get(`/Customers?filter={"where": {"role": "user", "createdAt": {"between": ["${filterdata?.from}", "${filterdata?.to}"]}}}`);
            const resInvoice = await get(`/Invoices?filter={"where": {"enrollOn" : { "between" :["${filterdata?.from}", "${filterdata?.to}"]}}}`);

            let finalEnrollX = [];
            let finalUserY = [];
            let finalInvoiceY = [];

            if (resUser?.statusCode === 200 && resInvoice?.statusCode === 200) {
                const users = resUser?.data || [];
                const invoices = resInvoice?.data || [];

                // Collect all unique dates from both users and invoices for the X-axis
                const allDatesSet = new Set([
                    ...users.map(u => formatDatecase(u.createdAt)),
                    ...invoices.map(inv => formatDatecase(inv.enrollOn))
                ]);

                // Standard chronological sort (by converting strings back to dates for comparison)
                const sortedDates = Array.from(allDatesSet).sort((a, b) => new Date(a) - new Date(b));
                finalEnrollX = sortedDates;
                setEnrollmentXLabel(finalEnrollX);

                // Aggregate statistics per date
                const userCounts = {};
                const enrollCounts = {};

                users.forEach(u => {
                    const date = formatDatecase(u.createdAt);
                    userCounts[date] = (userCounts[date] || 0) + 1;
                });

                invoices.forEach(inv => {
                    const date = formatDatecase(inv.enrollOn);
                    enrollCounts[date] = (enrollCounts[date] || 0) + 1;
                });

                finalUserY = sortedDates.map(date => userCounts[date] || 0);
                finalInvoiceY = sortedDates.map(date => enrollCounts[date] || 0);
                
                setUserYLabel(finalUserY);
                setInvoiceYLabel(finalInvoiceY);
            } else {
                setEnrollmentXLabel([]);
                setUserYLabel([]);
                setInvoiceYLabel([]);
            }

            // 3. Counters (Static but also up to date)
            const token = localStorage.getItem("accessToken");
            const totalUser = await get(`/Customers/count?where={"role": "user"}&access_token=${token}`);
            if (totalUser?.statusCode === 200) setTotalUsers(totalUser?.data?.count);

            const totalenroll = await get(`/UserPrograms/count?where={"status": "active"}&access_token=${token}`);
            if (totalenroll?.statusCode === 200) setTotalEnrollments(totalenroll?.data?.count);

            const totalprog = await get(`/Programs/count?access_token=${token}`);
            if (totalprog?.statusCode === 200) setTotalPrograms(totalprog?.data?.count);

            // Update Cache
            dashboardCache.filterdata = filterdata;
            dashboardCache.data = {
                paymentXLabel: xLabels,
                paymentYLabel: yLabels,
                enrollmentXLabel: finalEnrollX,
                userYLabel: finalUserY,
                invoiceYLabel: finalInvoiceY,
                totalUsers: totalUser?.data?.count || 0,
                totalEnrollments: totalenroll?.data?.count || 0,
                totalPrograms: totalprog?.data?.count || 0
            };

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        }
        setChartLoading(false);
    }, [filterdata]); // Forced rebuild trigger

    useEffect(() => {
        onGetDashboard();
    }, [onGetDashboard]);
    return (
        <div className="flex flex-col flex-1">
            <ModuleHeading title="Dashboard" />
            <Paper className="flex-1 p-3">
                <div className="flex flex-col justify-between md:flex-row">
                    <Typography variant="subtitle1" className=" font-bold">Sales Performance</Typography>
                    <Datepicker filterdata={filterdata} setFilterdata={setFilterdata} />
                </div>
                {/* CHART SECTION */}
                {chartLoading ? (
                    <div className="flex flex-col flex-1 md:flex-row mt-5 md:mt-0 lg:mt-5 gap-4">
                        <div className="flex flex-col flex-1">
                            <Skeleton variant="rounded" height={300} animation="wave" sx={{ borderRadius: '12px' }} />
                        </div>
                        <div className="flex flex-col flex-1">
                            <Skeleton variant="rounded" height={300} animation="wave" sx={{ borderRadius: '12px' }} />
                        </div>
                    </div>
                ) : (
                <div className="flex flex-col flex-1 md:flex-row mt-5 md:mt-0 lg:mt-5">
                    <div className="flex flex-col flex-1">
                        {/* <Typography className="  flex justify-center ">Enrollments vs New Users</Typography> */}
                        <LineChart
                            // width={500}
                            height={300}
                            series={[
                                {
                                    data: userYLabel,
                                    label: 'New Users',
                                    color: '#00b0ff',
                                },
                                {
                                    data: invoiceYLabel,
                                    label: 'Enrollments',
                                    color: '#8bc34a',
                                }
                            ]}
                            xAxis={[{ scaleType: 'point', data: enrollmentXLabel, label: 'Date' }]}
                        />
                    </div>
                    <div className="flex flex-col flex-1">
                        {/* <Typography className="  flex justify-center ">Income</Typography> */}
                        <LineChart
                            // width={500}
                            height={300}
                            series={[
                                {
                                    data: paymentYLabel,
                                    label: 'Income (Rs)',
                                    color: '#ffc400',
                                    area: true,
                                    // areaOpacity: 0.1,  
                                },
                            ]}
                            xAxis={[{ scaleType: 'point', data: paymentXLabel, label: 'Date' }]}
                        />

                    </div>
                </div>
                )}
                <hr className="mt-2" />
                <div className="flex flex-col gap-6 lg:flex-row  p-5 pb-2">
                    {/* Total Users */}
                    <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                        {chartLoading ? (
                            <Skeleton variant="rounded" width={40} height={28} animation="wave" />
                        ) : (
                            <Typography variant="h5" fontWeight={'bold'}>{totalUsers}</Typography>
                        )}
                        <Typography variant="body2" color="textSecondary">Total Users</Typography>
                    </Box>

                    {/* Enrollments */}
                    <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                        {chartLoading ? (
                            <Skeleton variant="rounded" width={40} height={28} animation="wave" />
                        ) : (
                            <Typography variant="h5" fontWeight={'bold'}>{totalEnrollments}</Typography>
                        )}
                        <Typography variant="body2" color="textSecondary">Enrollments Programs</Typography>
                    </Box>

                    {/* Total Programs */}
                    {/* <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                        <Typography variant="h5" fontWeight={'bold'}>{totalPrograms}</Typography>
                        <Typography variant="body2" color="textSecondary">Total Programs</Typography>
                    </Box> */}
                    {/* Online Users */}
                    {/* <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5" fontWeight={'bold'}>{onlineUsers}</Typography> <Typography variant="body2" color="textSecondary"> Online Users</Typography>
                    </Box> */}
                    {/* Total Income */}
                    {/* <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5" fontWeight={'bold'}>₹{totalIncome}</Typography> <Typography variant="body2" color="textSecondary">Total Income</Typography>
                    </Box> */}
                </div>
            </Paper>
            <Box
                display="flex"
                flexWrap="wrap"
                gap={3} // Similar to spacing in Grid
            >
                {/* Support Section */}
                <Box
                    flex="1 1 calc(50% - 24px)"
                    minWidth="300px"
                    display="flex"
                    flexDirection="column"
                >
                    <SupportDashboard parentLoading={chartLoading} />
                </Box>

                {/* Recently Enrolled Programs */}
                <Box
                    flex="1 1 calc(50% - 24px)"
                    minWidth="300px"
                    display="flex"
                    flexDirection="column"
                >
                    <DashboardEnroll parentLoading={chartLoading} />
                </Box>

                {/* Programs */}
                <Box
                    flex="1 1 calc(50% - 24px)"
                    minWidth="300px"
                    display="flex"
                    flexDirection="column"
                >
                    <ProgramsList parentLoading={chartLoading} />
                </Box>

                {/* Recently Registered Users */}
                <Box
                    flex="1 1 calc(50% - 24px)"
                    minWidth="300px"
                    display="flex"
                    flexDirection="column"
                >
                    <Registration parentLoading={chartLoading} />
                </Box>
            </Box>

        </div>
    );
};

export default Dashboard;
